// components/oatt-header/OattHeader.tsx
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AccountInfo } from '@azure/msal-browser';
import styles from './OattHeader.module.css';

type OattHeaderProps = {
  readonly account?: (AccountInfo & { name?: string; username?: string }) | null;
  readonly onLogout?: () => void;
  readonly isOattBillPreparer?: boolean;
  readonly isOattBillApprover?: boolean;
  readonly isOattRateOwner?: boolean;
};

const baseFlyouts = [
  {
    key: 'dashboard',
    label: 'DASHBOARDS',
    links: [
      { to: '/oati/dashboard', text: 'Reservation Data' },
      { to: '/oati/computation', text: 'QC Computation' },
      { to: '/oati/validation', text: 'Validation' },

    ],
  },

  {
    key: 'invoice',
    label: 'INVOICE',
    links: [{ to: '/oati/invoice', text: 'OATT Invoice' }],
  },
  {
    key: 'core_info',
    label: 'CORE INFO',
    links: [
      { to: '/oati/core-info/rates', text: 'Rates' },
      { to: '/oati/core-info/vendor-info', text: 'Vendor Info' },
      { to: '/oati/nsp-core-info', text: 'NSP Core Info' },
      { to: '/oati/audit', text: 'Audit' },
    ],
  },
];

export default function OattHeader({ account, onLogout, isOattBillPreparer, isOattBillApprover, isOattRateOwner }: OattHeaderProps) {
  const flyouts = baseFlyouts
    .filter((flyout) => !isOattRateOwner || flyout.key === 'core_info')
    .map((flyout) => {
      if (flyout.key !== 'core_info') return flyout;
      return {
        ...flyout,
        links: flyout.links.filter((link) => {
          if (link.to === '/oati/nsp-core-info' && !isOattBillPreparer && !isOattBillApprover) return false;

          if (
            isOattRateOwner &&
            (
              link.to === '/oati/audit' ||
              link.to === '/oati/core-info/vendor-info'
            )
          ) {
            return false;
          }

          return true;
        }),
      };
    });
  const [hoveredFlyout, setHoveredFlyout] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const userName = account?.name ?? account?.username;

  const handleMouseEnter = (key: string) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setHoveredFlyout(key);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => setHoveredFlyout(null), 150);
    setHoverTimeout(timeout);
  };

  const isFlyoutActive = (flyoutKey: string) => {
    const flyout = flyouts.find((f) => f.key === flyoutKey);
    return flyout?.links.some((link) => location.pathname.startsWith(link.to));
  };

  return (
    <header className={styles.header} data-testid="oatt-header">
      <div className={styles.topBar} />

      <nav className={styles.mainNav}>
        <div className={styles.container}>
          {/* Logo */}
          <div className={styles.logoWrapper}>
            <img
              src="/Logo-onwhite.png"
              alt="Nova Scotia Power"
              className={styles.logo}
            />
            <span className={styles.brandTitle}>Windvoice Calculator</span>
          </div>

          {/* Nav + User */}
          <div className={styles.menu}>
            <div className={styles.flyoutContainer} onMouseLeave={handleMouseLeave}>
              {flyouts.map((flyout) => (
                <button
                  key={flyout.key}
                  type="button"
                  className={`${styles.flyout} ${isFlyoutActive(flyout.key) ? styles.active : ''}`}
                  onMouseEnter={() => handleMouseEnter(flyout.key)}
                  onFocus={() => handleMouseEnter(flyout.key)}
                  onBlur={handleMouseLeave}
                  aria-expanded={hoveredFlyout === flyout.key}
                  aria-haspopup="true"
                  data-testid="oatt-nav-menu"
                  data-menu={flyout.key}
                  data-active={String(Boolean(isFlyoutActive(flyout.key)))}
                >
                  <span>{flyout.label}</span>

                  <div
                    className={styles.flyoutContent}
                    style={{ display: hoveredFlyout === flyout.key ? 'block' : 'none' }}
                    role="menu"
                  >
                    {flyout.links.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        role="menuitem"
                        className={location.pathname === link.to ? styles.activeLink : ''}
                        data-testid="oatt-nav-link"
                        data-path={link.to}
                      >
                        {link.text}
                      </Link>
                    ))}
                  </div>
                </button>
              ))}
            </div>

            <div className={styles.userBar}>
              <span className={styles.userGreeting} data-testid="oatt-user-name">Hello, {userName}</span>
              <button className={styles.logoutBtn} onClick={onLogout} data-testid="oatt-logout-button">
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
